import * as React from "react";

/**
 * DocSidebar Layout Configuration
 * 
 * Single source of truth for sidebar section composition (order + visibility).
 * This is a lightweight "composition config" - NOT a full schema system.
 * 
 * To change sidebar order: edit DOC_SIDEBAR_LAYOUT array.
 * To hide a section conditionally: add showWhen/requires predicates.
 * 
 * Rules:
 * - This is a composition module (React-aware, but no hooks allowed)
 * - No runtime validation (config is static constants)
 * - No doctype-specific layout rules (sidebar is generic across all doctypes)
 */

import type { DocSidebarData, DocSidebarPublicShare } from "@/lib/erp/docs";
import { DocSidebarCoverMediaSection } from "../DocSidebarCoverMediaSection";
import { DocSidebarAssignmentsSection } from "../DocSidebarAssignmentsSection";
import { DocSidebarShareSection } from "../DocSidebarShareSection";
import { DocSidebarTagsSection } from "../DocSidebarTagsSection";
import { DocSidebarAttachmentsSection } from "../DocSidebarAttachmentsSection";
import { DocSidebarHistorySection } from "../DocSidebarHistorySection";

/**
 * Sidebar section identifiers.
 * Must match keys in DOC_SIDEBAR_SECTIONS registry.
 */
export type SidebarSectionId =
  | "coverMedia"
  | "assignments"
  | "share"
  | "tags"
  | "attachments"
  | "history";

/**
 * Render context passed to section renderers.
 */
export type SidebarRenderCtx = {
  doctype: string;
  name: string;
  editable: boolean;
};

/**
 * Configuration for a single sidebar section.
 */
export type SidebarSectionConfig = {
  /** Section identifier */
  id: SidebarSectionId;
  /** Optional: hide section if predicate returns false */
  showWhen?: (ctx: SidebarRenderCtx) => boolean;
  /** Optional: hide section if data requirement not met */
  requires?: (data: DocSidebarData) => boolean;
};

/**
 * Sidebar section renderer function signature.
 */
export type SidebarSectionRenderer = (args: {
  ctx: SidebarRenderCtx;
  data: DocSidebarData;
  publicShare: DocSidebarPublicShare;
  openImageLightbox: () => void;
}) => React.JSX.Element | null;

/**
 * Sidebar layout configuration.
 * 
 * This array defines:
 * - Section order (top to bottom)
 * - Visibility rules (showWhen/requires predicates)
 * 
 * To reorder sections: rearrange this array.
 * To hide a section: add showWhen/requires predicate.
 */
export const DOC_SIDEBAR_LAYOUT: SidebarSectionConfig[] = [
  { id: "assignments" },
  { id: "share" },
  { id: "tags" },
  { id: "attachments" },
];

/**
 * Section registry: maps section ID to renderer component.
 * 
 * Follows the same pattern as EXPERIENCE_WIDGETS registry.
 * Each renderer receives normalized props from DocSidebarLive.
 */
export const DOC_SIDEBAR_SECTIONS: Record<SidebarSectionId, SidebarSectionRenderer> = {
  coverMedia: ({ ctx, data, openImageLightbox }) => (
    <DocSidebarCoverMediaSection
      doctype={ctx.doctype}
      name={ctx.name}
      image={data.image}
      primaryVideo={data.primaryVideo}
      editable={ctx.editable}
      onImageClick={openImageLightbox}
    />
  ),

  assignments: ({ ctx, data }) => (
    <DocSidebarAssignmentsSection
      doctype={ctx.doctype}
      name={ctx.name}
      assigned_to={data.assigned_to}
      editable={ctx.editable}
    />
  ),

  share: ({ ctx, data, publicShare }) => (
    <DocSidebarShareSection
      doctype={ctx.doctype}
      name={ctx.name}
      shares={data.shares}
      public_share={publicShare}
      share_capabilities={data.share_capabilities}
      liked_by={data.liked_by}
      like_count={data.like_count}
      comment_count={data.comment_count}
      followed={data.is_followed}
      editable={ctx.editable}
    />
  ),

  tags: ({ ctx, data }) => (
    <DocSidebarTagsSection
      doctype={ctx.doctype}
      name={ctx.name}
      tags={data.tags}
      editable={ctx.editable}
    />
  ),

  attachments: ({ ctx, data }) => (
    <DocSidebarAttachmentsSection
      doctype={ctx.doctype}
      name={ctx.name}
      editable={ctx.editable}
      sidebarAttachments={data.attachments}
    />
  ),

  history: ({ ctx }) => (
    <DocSidebarHistorySection doctype={ctx.doctype} name={ctx.name} />
  ),
};
